import {TitleCasePipe} from '@angular/common';
import {HttpErrorResponse} from '@angular/common/http';
import {Component, computed, inject, Signal} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatRadioModule} from '@angular/material/radio';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatStepperModule} from '@angular/material/stepper';
import {Router, RouterLink} from '@angular/router';
import {
  buildResourceDetailsFields,
  createFormGroup,
  createFormlyOptions,
} from '@app/resource-explorer/resource.form.config';
import {CategoryFilter} from '@app/search/filters/resources/category.filter';
import {prepareFormPayload} from '@forms/utils/form-utils';
import {ValidationErrorHandler} from '@forms/validationerrorhandler.service';
import {CategoryService, ContactBase, ResourceService} from '@middleware';
import {IconService} from '@middleware/services/icon/icon.service';
import {FormlyFieldConfig, FormlyModule} from '@ngx-formly/core';
import {UserService} from '@services/user/user.service';
import {
  SectionHeaderAction,
  SectionHeaderActions,
} from '@shared/components/section-header-actions/section-header-actions';
import {stripPhoneNumber} from '@validators/phone-number.validator';
import {lastValueFrom} from 'rxjs';

@Component({
  selector: 'app-resource-add',
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    MatStepperModule,
    MatRadioModule,
    MatIconModule,
    MatButtonModule,
    RouterLink,
    TitleCasePipe,
  ],
  templateUrl: './resource-add.component.html',
  styleUrl: './resource-add.component.scss',
})
export class ResourceAddComponent implements SectionHeaderActions {
  typeForm = new FormGroup({
    type: new FormControl(null, Validators.required),
  });

  form = createFormGroup();
  options = createFormlyOptions();
  formFields: FormlyFieldConfig[] = [];
  model: any = {};
  submitting = false;
  private readonly categoryFilter = inject(CategoryFilter);

  constructor(
    private readonly snackbarService: MatSnackBar,
    private readonly userService: UserService,
    private readonly categoryService: CategoryService,
    private readonly iconService: IconService,
    private readonly resourceService: ResourceService,
    private readonly router: Router,
    private readonly validationErrorHandler: ValidationErrorHandler,
  ) {}

  onTypeChange(type: 'Generic' | 'Event') {
    this.formFields = buildResourceDetailsFields({
      modelType: type,
      creatorOrganization: this.userService.userSignal().organization.full_name,
      categoryService: this.categoryService,
      iconService: this.iconService,
      categoryFilter: this.categoryFilter,
    });

    this.model = {
      resource_type: type,
      visible_to_all_users: false,
      owners: [this.userService.userSignal().email],
      target_audiences: [this.userService.userSignal().organization.name],
      contacts: [{}],
      spatial_coverage: {},
    };
  }

  async submit(event: Event): Promise<void> {
    event.preventDefault();
    if (this.form.status == 'PENDING') {
      this.snackbarService.open("We're making sure everything's alright, try again in a few seconds.", undefined, {
        duration: 5000,
      });
      return;
    } else if (!this.form.valid) {
      this.validationErrorHandler.handleError(this.formFields);
      return;
    }
    try {
      this.submitting = true;
      const sanitizedForm = await prepareFormPayload(this.model, this.formFields);
      if (sanitizedForm.contacts) {
        sanitizedForm.contacts = sanitizedForm.contacts.map((c: ContactBase) => ({
          ...c,
          phone_number: c.phone_number ? stripPhoneNumber(c.phone_number) : c.phone_number,
        }));
      }
      const response = await lastValueFrom(this.resourceService.createResource(sanitizedForm, {observe: 'response'}));
      if (!response.ok) throw response;

      this.form.markAsPristine();
      (this as any).__skipUnsavedGuard = true;

      const location = response.headers.get('Location');
      const id = location?.split('/').pop();
      setTimeout(() => {
        this.router.navigate(['/resource-explorer/view', id]);
      });
    } catch (e) {
      this.validationErrorHandler.handleError(this.formFields, <HttpErrorResponse>e);
    } finally {
      this.submitting = false;
      setTimeout(() => ((this as any).__skipUnsavedGuard = false), 1000);
    }
  }

  public sectionHeaderActions: Signal<SectionHeaderAction[]> = computed(() => {
    const actions: SectionHeaderAction[] = [
      {
        target: '/help/resource-explorer/add-resource',
        targetTab: '_blank',
        caption: 'Help',
        color: 'var(--mat-sys-primary)',
        icon: 'help_outline',
      },
    ];

    return actions;
  });
}
